import { useState } from "react";
import { Check, Pencil, Plus, Trash2 } from "lucide-react";

import type { IssueStatus } from "@/domains/pm/types";
import { StatusBadge } from "./StatusBadge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { visibleStatuses } from "@/domains/pm/statuses/statusCatalogue";
import { StatusManagementDialogs } from "@/domains/pm/statuses/StatusManagementDialogs";
import { useCustomStatusRow } from "@/domains/pm/statuses/useCustomStatusRow";

interface StatusPickerProps {
  value: IssueStatus;
  onChange: (status: IssueStatus) => void;
  /** The issue's building project. Required to offer "New status", since a
   *  status belongs to exactly one project. */
  buildingProjectId?: number | null;
  /** Read the status without being able to change it — snapshot mode. */
  disabled?: boolean;
  /** Sizing/border override for the trigger button. Defaults to a compact
   *  bordered control; the issues-list row cell passes a borderless variant. */
  triggerClassName?: string;
  /** Badge size for the trigger. `lg` enlarges the label + dot ~35% for the
   *  expanded issue panel's meta bar. */
  size?: "sm" | "lg";
  /** Passed through to the trigger's StatusBadge — `pill` gives the tinted
   *  background used in the issue detail panel's header (WHA-3810). */
  badgeVariant?: "bare" | "pill";
}

/**
 * StatusPicker — a dropdown status selector: a trigger showing the current
 * StatusBadge, opening a Popover with the status option list. Shared by the
 * issues-list row cell and the expanded-view meta bar so both edit status
 * identically (mirrors DisciplinePicker).
 *
 * The list ends with "New status", and each custom status carries edit and
 * delete affordances. That is the whole of status management — there is no
 * settings page — because a status is almost always wanted at the moment you
 * find yourself without it.
 */
export function StatusPicker({
  value,
  onChange,
  buildingProjectId,
  disabled = false,
  triggerClassName,
  size,
  badgeVariant,
}: StatusPickerProps) {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const { catalogue } = useStatusCatalogue();
  const { canManagePMOnProject } = usePermissions();
  const statusAdmin = useCustomStatusRow(buildingProjectId ?? null);

  const options = visibleStatuses(catalogue, buildingProjectId);
  const mayManage = buildingProjectId != null && canManagePMOnProject(buildingProjectId);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            disabled={disabled}
            type="button"
            className={cn(
              "inline-flex items-center focus:outline-none focus:ring-1 focus:ring-ring",
              triggerClassName ?? "h-7 rounded-md border border-input px-2 hover:bg-accent"
            )}
          >
            <StatusBadge
              status={value}
              buildingProjectId={buildingProjectId}
              size={size}
              variant={badgeVariant}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-52 p-1" align="start">
          {options.map((option) => (
            <div key={option.key} className="group flex items-center rounded hover:bg-muted">
              <button
                type="button"
                onClick={() => {
                  if (option.key !== value) onChange(option.key);
                  setOpen(false);
                }}
                className="flex min-w-0 flex-1 items-center justify-between gap-2 px-2 py-1.5 text-xs"
              >
                <StatusBadge status={option.key} buildingProjectId={buildingProjectId} />
                {option.key === value && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
              {option.custom && mayManage && (
                <span className="flex shrink-0 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100">
                  <button
                    type="button"
                    aria-label={`${t.common.edit} ${option.label}`}
                    className="rounded p-1 hover:bg-accent"
                    onClick={() => {
                      setOpen(false);
                      statusAdmin.startEdit(option.key);
                    }}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    aria-label={`${t.common.delete} ${option.label}`}
                    className="rounded p-1 hover:bg-accent"
                    onClick={() => {
                      setOpen(false);
                      statusAdmin.startDelete(option.key);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
          ))}

          {mayManage && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                statusAdmin.startCreate();
              }}
              className="mt-1 flex w-full items-center gap-2 rounded border-t px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
              {t.pm.issues.statuses.newStatus}
            </button>
          )}
        </PopoverContent>
      </Popover>

      <StatusManagementDialogs admin={statusAdmin} onCreated={onChange} />
    </>
  );
}
